// src/modals/ProrataSplitModal.jsx — Lot 5c PRORATA (UI VISIBLE du 5e calque).
//
// Modale de répartition au prorata d'une réservation V2 sur les mois qu'elle
// touche. Composant PUR : toutes les données arrivent par props, il ne touche
// JAMAIS `data` ni Supabase (le parent — App.jsx — pose/retire le calque via
// onSet/onClear). La modale n'est qu'un éditeur de `spec` + un APERÇU LIVE.
//
// CHEMIN ARGENT — fail-closed UI : le bouton Enregistrer reste désactivé tant
// que (a) un mois est gelé, (b) la résolution renvoie null, (c) en manuel la
// somme ≠ total au centime, ou (d) le garde-fou posableFn refuse. La validation
// d'autorité reste côté garde-fous 5b (assertProrataPosable, pré-bindé en
// `posableFn`) ; la modale NE DUPLIQUE PAS la logique de gel/XOR/Σ.
//
// MACHINE D'ÉTAT (init depuis l'objet BRUT existingSpec — AMENDEMENT M3) :
//   mode     : existingSpec?.mode || 'auto'
//   excluded : Set(existingSpec?.excluded || [])         (mode auto)
//   amounts  : { mk: '...' } CHAÎNES (existingSpec?.amounts) — '' ≠ 0 (mode manuel)
//
// AMENDEMENT M1 (BLOQUANT) : si frozenSet non vide → ÉTAT TERMINAL (aucune
// checklist, aucun Enregistrer ; un mois facturé gèle toute la résa, le moteur
// fail-close globalement).

import { useState } from "react"
import { Scale, AlertTriangle } from "lucide-react"
import { DS } from "../lib/ui"
import { resolveProrataParts } from "../lib/prorata-resolve"

import useEchapPourFermer from "../lib/useEchapPourFermer"
const ACCENT = DS.terra || "#C17B5C" // pastille/accent prorata (terracotta DS)

// round2 : arrondi 2 décimales (centime). Source unique du module.
function round2(n) {
  return Math.round(n * 100) / 100
}

// fmtMoney2 : montant FR 2 décimales (affichage seul).
const fmtMoney2 = (n) =>
  (Number(n) || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })

// parseAmounts : { mk: '...chaîne...' } → { mk: number } pour les seules
// chaînes non vides ET finies (une chaîne vide '' n'est PAS un 0 — pattern
// ResaModal). Une virgule décimale FR est tolérée.
function parseAmounts(amountsStr) {
  const out = {}
  if (!amountsStr || typeof amountsStr !== "object") return out
  for (const mk of Object.keys(amountsStr)) {
    const raw = amountsStr[mk]
    if (raw == null || String(raw).trim() === "") continue
    const n = Number(String(raw).replace(",", "."))
    if (Number.isFinite(n)) out[mk] = n
  }
  return out
}

export default function ProrataSplitModal({
  lid,
  code,
  logNom,
  voyageur,
  naturalResa,
  naturalMontant,
  montantTotal,
  monthsTouched,
  monthLabels,
  frozenSet,
  existingSpec,
  posableFn,
  onSet,
  onClear,
  onClose,
}) {
  // Échap ferme — consigne 2026-08-14, hook partagé (pile anti-double-fermeture).
  useEchapPourFermer(onClose)

  // ── État (M3 : lecture de l'objet BRUT existingSpec, jamais reconstruit) ──
  const [mode, setMode] = useState(existingSpec?.mode || "auto")
  const [excluded, setExcluded] = useState(() => new Set(existingSpec?.excluded || []))
  const [amounts, setAmounts] = useState(() => {
    // Convertit existingSpec.amounts (nombres) en CHAÎNES pour l'édition.
    const src = existingSpec?.amounts && typeof existingSpec.amounts === "object" ? existingSpec.amounts : {}
    const out = {}
    for (const mk of Object.keys(src)) out[mk] = String(src[mk])
    return out
  })

  // labelFor : mk → libellé FR (via monthLabels aligné sur monthsTouched).
  const labelFor = (mk) => {
    const i = Array.isArray(monthsTouched) ? monthsTouched.indexOf(mk) : -1
    return i >= 0 && Array.isArray(monthLabels) ? monthLabels[i] : mk
  }

  // ── AMENDEMENT M1 (BLOQUANT) — état terminal si un mois est gelé ──
  const hasFrozen = !!(frozenSet && typeof frozenSet.has === "function"
    ? frozenSet.size > 0
    : Array.isArray(frozenSet) && frozenSet.length > 0)
  const firstFrozenLabel = (() => {
    if (!hasFrozen) return null
    const arr = frozenSet && typeof frozenSet.has === "function" ? [...frozenSet] : frozenSet
    const mk = Array.isArray(arr) ? arr.find((m) => m) : null
    return mk ? labelFor(mk) : null
  })()

  // ── Shell de la modale (en-tête + fermeture) ──
  const shell = (children) => (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.55)", zIndex: 999, display: "flex", alignItems: "flex-end", justifyContent: "center" }}>
      <div className="fbz-modal-sheet" style={{ background: DS.card, borderRadius: "20px 20px 0 0", padding: 24, width: "100%", maxWidth: 480, maxHeight: "90vh", overflowY: "auto", fontFamily: DS.fontBody }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 8, fontFamily: DS.fontDisplay, fontWeight: 600, color: DS.marineDark, fontSize: 17 }}>
              <Scale size={18} strokeWidth={2.2} color={ACCENT} /> Répartir au prorata
            </div>
            <div style={{ fontSize: 11, color: DS.textMuted, marginTop: 2 }}>
              {logNom}{voyageur ? ` · ${voyageur}` : ""}
            </div>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", fontSize: 24, cursor: "pointer", color: DS.textMuted, lineHeight: 1 }}>×</button>
        </div>
        {children}
      </div>
    </div>
  )

  if (hasFrozen) {
    // État terminal M1 : pas de checklist, pas d'Enregistrer — Fermer uniquement.
    return shell(
      <>
        <div style={{ display: "flex", gap: 9, alignItems: "flex-start", marginBottom: 16, padding: "12px 14px", background: DS.warningBg, border: `1px solid ${DS.warning}55`, borderRadius: DS.radiusCard, fontSize: 13, color: DS.warningText, lineHeight: 1.5 }}>
          <AlertTriangle size={16} strokeWidth={2.2} style={{ flexShrink: 0, marginTop: 2 }} /><span>Cette réservation touche un mois déjà facturé{firstFrozenLabel ? ` (${firstFrozenLabel})` : ""}.
          La répartition au prorata est impossible tant que la facture n'est pas annulée.</span>
        </div>
        <div style={{ display: "flex" }}>
          <button className="fbz-press" onClick={onClose} style={{ flex: 1, padding: 12, background: DS.card, color: DS.marine, border: `0.5px solid ${DS.borderHover}`, borderRadius: DS.radiusControl, fontWeight: 600, cursor: "pointer", fontFamily: DS.fontBody }}>
            Fermer
          </button>
        </div>
      </>
    )
  }

  // ── spec courant (dérivé de l'état) — source unique preview + garde-fou ──
  const spec =
    mode === "auto"
      ? { mode: "auto", excluded: [...excluded] }
      : { mode: "manual", amounts: parseAmounts(amounts) }

  // ── PREVIEW LIVE — B1 : naturalResa (dates pleines) + montantTotal (scalaire
  //    post-override), JAMAIS l'objet projeté de la ligne. ──
  const preview = resolveProrataParts(naturalResa, montantTotal, spec, monthsTouched, frozenSet)
  const previewImpossible = preview === null

  // ── Total verrouillé (mode manuel) : Σ parseAmounts vs montantTotal ──
  const parsed = parseAmounts(amounts)
  const sumManual = round2(Object.keys(parsed).reduce((s, mk) => s + (Number(parsed[mk]) || 0), 0))
  const ecart = round2(sumManual - (Number(montantTotal) || 0))
  // Un montant négatif est invalide (le moteur le rejette) : on le traite comme un
  // écart, sinon « +750 / −50 » afficherait un écart 0 « vert » contredisant la
  // bannière « répartition impossible ».
  const hasNegativeManual = mode === "manual" && Object.keys(parsed).some((mk) => parsed[mk] < 0)
  const sumMismatch = mode === "manual" && (Math.abs(ecart) > 0.01 || hasNegativeManual)

  // ── Garde-fou (anti-duplication) : closure pré-bindée sur assertProrataPosable ──
  const guard = typeof posableFn === "function" ? posableFn(spec) : { ok: true }

  // Enregistrer activé seulement si TOUT est vert (fail-closed).
  const canSave = !previewImpossible && !sumMismatch && guard.ok

  // toggleExcluded : coche/décoche un mois en mode auto (décoché = exclu).
  const toggleExcluded = (mk) => {
    setExcluded((prev) => {
      const next = new Set(prev)
      if (next.has(mk)) next.delete(mk)
      else next.add(mk)
      return next
    })
  }

  return shell(
    <>
      {/* En-tête : base de répartition = total POST-OVERRIDE (MINEUR-2) */}
      <div style={{ marginBottom: 14, fontSize: 13, color: DS.textSecondary }}>
        Base de répartition : <strong style={{ color: DS.marine }}>{fmtMoney2(montantTotal)} €</strong>
      </div>

      {/* Toggle mode auto / manuel */}
      <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
        {[
          { k: "auto", label: "Au prorata des nuits" },
          { k: "manual", label: "Montants manuels" },
        ].map((m) => (
          <button
            key={m.k}
            className="fbz-press"
            onClick={() => setMode(m.k)}
            style={{
              flex: 1,
              padding: "9px 4px",
              border: `1.5px solid ${mode === m.k ? ACCENT : DS.border}`,
              borderRadius: DS.radiusControl,
              background: mode === m.k ? ACCENT + "15" : DS.card,
              color: mode === m.k ? ACCENT : DS.textMuted,
              fontWeight: 700,
              fontSize: 12,
              cursor: "pointer",
              fontFamily: DS.fontBody,
            }}
          >
            {m.label}
          </button>
        ))}
      </div>

      {/* Liste des mois touchés (mode auto : cases / mode manuel : inputs) */}
      <div style={{ marginBottom: 14 }}>
        {(monthsTouched || []).map((mk) => {
          const part = preview ? preview[mk] : undefined
          return (
            <div
              key={mk}
              style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0", borderBottom: `1px solid ${DS.border}` }}
            >
              {mode === "auto" ? (
                <label style={{ display: "flex", alignItems: "center", gap: 8, flex: 1, cursor: "pointer", fontSize: 13, color: DS.textPrimary }}>
                  <input
                    type="checkbox"
                    checked={!excluded.has(mk)}
                    onChange={() => toggleExcluded(mk)}
                    style={{ accentColor: ACCENT, width: 16, height: 16 }}
                  />
                  <span>{labelFor(mk)}</span>
                </label>
              ) : (
                <div style={{ flex: 1, display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ fontSize: 13, color: DS.textPrimary, minWidth: 110 }}>{labelFor(mk)}</span>
                  <input
                    type="number"
                    value={amounts[mk] != null ? amounts[mk] : ""}
                    onChange={(e) => setAmounts((prev) => ({ ...prev, [mk]: e.target.value }))}
                    placeholder="0.00"
                    style={{ border: `0.5px solid ${DS.borderHover}`, borderRadius: DS.radiusControl, padding: "7px 10px", width: 110, fontSize: 13, outline: "none", fontFamily: DS.fontBody, background: DS.card }}
                  />
                </div>
              )}
              {/* APERÇU LIVE de la part résolue pour ce mois */}
              <span style={{ fontSize: 13, fontWeight: 700, color: part != null ? DS.marine : DS.textMuted, minWidth: 80, textAlign: "right" }}>
                {part != null ? `${fmtMoney2(part)} €` : "—"}
              </span>
            </div>
          )
        })}
      </div>

      {/* Total verrouillé (mode manuel) : écart Σ − total */}
      {mode === "manual" && (
        <div style={{ marginBottom: 12, fontSize: 12, display: "flex", justifyContent: "space-between", color: sumMismatch ? DS.danger : DS.textSecondary }}>
          <span>Somme saisie : <strong>{fmtMoney2(sumManual)} €</strong></span>
          <span>
            Écart :{" "}
            <strong style={{ color: sumMismatch ? DS.danger : DS.successText }}>
              {ecart > 0 ? "+" : ""}{fmtMoney2(ecart)} €
            </strong>
          </span>
        </div>
      )}

      {/* Bannière « répartition impossible » (résolution null) */}
      {previewImpossible && (
        <div style={{ marginBottom: 12, padding: "9px 12px", background: DS.dangerBg, border: `1px solid ${DS.danger}44`, borderRadius: 8, fontSize: 12, color: DS.dangerText }}>
          Répartition impossible avec cette saisie (aucun mois conservé, ou montants incohérents).
        </div>
      )}

      {/* Message inline du garde-fou 5b (gel / XOR / Σ) si refus */}
      {!guard.ok && guard.message && (
        <div style={{ marginBottom: 12, padding: "9px 12px", background: DS.dangerBg, border: `1px solid ${DS.danger}44`, borderRadius: 8, fontSize: 12, color: DS.dangerText }}>
          {guard.message}
        </div>
      )}

      {/* Actions : Retirer (si calque existant) · Annuler · Enregistrer */}
      <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
        {existingSpec && (
          <button
            className="fbz-press"
            onClick={onClear}
            style={{ flex: 1, padding: 12, background: DS.corailSoft, color: DS.corailText, border: `0.5px solid ${DS.corail}55`, borderRadius: DS.radiusControl, fontWeight: 600, cursor: "pointer", fontFamily: DS.fontBody, fontSize: 12 }}
          >
            Retirer la répartition
          </button>
        )}
        <button className="fbz-press" onClick={onClose} style={{ flex: 1, padding: 12, background: DS.card, color: DS.marine, border: `0.5px solid ${DS.borderHover}`, borderRadius: DS.radiusControl, fontWeight: 600, cursor: "pointer", fontFamily: DS.fontBody }}>
          Fermer
        </button>
        <button
          className="fbz-press"
          onClick={() => onSet(spec)}
          disabled={!canSave}
          style={{ flex: 2, padding: 12, background: DS.corail, color: "#fff", border: "none", borderRadius: DS.radiusControl, fontWeight: 700, cursor: canSave ? "pointer" : "not-allowed", fontFamily: DS.fontBody, opacity: canSave ? 1 : 0.5 }}
        >
          Enregistrer
        </button>
      </div>
    </>
  )
}
